import React, { useState } from 'react'

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Button,
    Typography,
    Box,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'

import { InternalInvoice, InternalInvoiceItem } from '../../../models'
import {
    AddLineItemRequest,
    EditLineItemRequest,
    RemoveLineItemRequest,
} from '../../../hooks/useBilling'
import { formatCurrency } from './helpers'
import { AddLineItemDialog } from './AddLineItemDialog'
import { EditLineItemDialog } from './EditLineItemDialog'
import { DeleteLineItemDialog } from './DeleteLineItemDialog'

interface LineItemsTableProps {
    invoice: InternalInvoice
    onAddLineItem: (data: AddLineItemRequest) => Promise<void>
    onEditLineItem: (data: EditLineItemRequest) => Promise<void>
    onRemoveLineItem: (data: RemoveLineItemRequest) => Promise<void>
    viewOnly?: boolean
    isAddLineItemLoading?: boolean
    isEditLineItemLoading?: boolean
    isRemoveLineItemLoading?: boolean
}

export const LineItemsTable: React.FC<LineItemsTableProps> = ({
    invoice,
    onAddLineItem,
    onEditLineItem,
    onRemoveLineItem,
    viewOnly = false,
    isAddLineItemLoading = false,
    isEditLineItemLoading = false,
    isRemoveLineItemLoading = false,
}) => {
    const theme = useTheme()
    const [showAddDialog, setShowAddDialog] = useState(false)
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [
        selectedLineItem,
        setSelectedLineItem,
    ] = useState<InternalInvoiceItem | null>(null)

    const handleEditClick = (lineItem: InternalInvoiceItem) => {
        setSelectedLineItem(lineItem)
        setShowEditDialog(true)
    }

    const handleDeleteClick = (lineItem: InternalInvoiceItem) => {
        setSelectedLineItem(lineItem)
        setShowDeleteDialog(true)
    }

    const handleConfirmDelete = async () => {
        if (selectedLineItem) {
            await onRemoveLineItem({
                invoiceId: invoice.id,
                lineItemId: selectedLineItem.id,
            })
        }
    }

    const handleAddLineItem = async (data: {
        description: string
        subtotal?: string
        quantity?: string
        unit_price?: string
    }) => {
        await onAddLineItem({
            invoiceId: invoice.id,
            ...data,
        })
        setShowAddDialog(false)
    }

    const handleEditLineItem = async (data: {
        description?: string
        quantity?: string
        unit_price?: string
        subtotal?: string
    }) => {
        if (selectedLineItem) {
            await onEditLineItem({
                invoiceId: invoice.id,
                lineItemId: selectedLineItem.id,
                ...data,
            })
            setShowEditDialog(false)
            setSelectedLineItem(null)
        }
    }

    return (
        <Box>
            {/* Header with Add Button */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <Typography variant="h6">Line Items</Typography>
                {!viewOnly && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setShowAddDialog(true)}
                        disabled={isAddLineItemLoading}
                    >
                        Add Line Item
                    </Button>
                )}
            </Box>

            {/* Table */}
            <TableContainer component={Paper} elevation={1}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">Subtotal</TableCell>
                            {!viewOnly && (
                                <TableCell align="center">Actions</TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {invoice.items && invoice.items.length > 0 ? (
                            invoice.items.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {item.description}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {item.quantity || '-'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {item.unit_price
                                            ? formatCurrency(item.unit_price)
                                            : '-'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography
                                            variant="body2"
                                            style={{ fontWeight: 'bold' }}
                                        >
                                            {formatCurrency(item.subtotal)}
                                        </Typography>
                                    </TableCell>
                                    {!viewOnly && (
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleEditClick(item)
                                                }
                                                disabled={isEditLineItemLoading}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleDeleteClick(item)
                                                }
                                                disabled={
                                                    isRemoveLineItemLoading
                                                }
                                                color="secondary"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={viewOnly ? 4 : 5}
                                    align="center"
                                >
                                    <Typography
                                        color="textSecondary"
                                        variant="body2"
                                    >
                                        No line items found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Summary Row */}
            {invoice.items && invoice.items.length > 0 && (
                <Box
                    mt={2}
                    p={2}
                    bgcolor={theme.palette.grey[50]}
                    borderRadius={1}
                >
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography variant="h6">Summary</Typography>
                        <Box textAlign="right">
                            <Typography variant="h6" color="primary">
                                Total: {formatCurrency(invoice.total_amount)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Add Line Item Dialog */}
            <AddLineItemDialog
                open={showAddDialog}
                onClose={() => setShowAddDialog(false)}
                onAddLineItem={handleAddLineItem}
                isLoading={isAddLineItemLoading}
            />

            {/* Edit Line Item Dialog */}
            <EditLineItemDialog
                open={showEditDialog}
                onClose={() => {
                    setShowEditDialog(false)
                    setSelectedLineItem(null)
                }}
                onEditLineItem={handleEditLineItem}
                lineItem={selectedLineItem}
                isLoading={isEditLineItemLoading}
            />

            {/* Delete Line Item Dialog */}
            <DeleteLineItemDialog
                open={showDeleteDialog}
                onClose={() => {
                    setShowDeleteDialog(false)
                    setSelectedLineItem(null)
                }}
                onConfirm={handleConfirmDelete}
                lineItemDescription={selectedLineItem?.description || ''}
                isLoading={isRemoveLineItemLoading}
            />
        </Box>
    )
}
